import type { AnyAction, Dispatch } from 'redux';

import {
  duplicateFileErrorDialog,
  generalErrorDialog,
  MaxLimitFileErrorDialog,
  removeFileDialog,
  uploadErrorDialog,
  uploadFileSizeErrorDialog
} from '../../shared/dialogConfigs';
import { clearStagingAttachments, removeAttachments, updateAttachmentsValidation } from '../../store/attachments';
import { openDialog } from '../../store/dialog';
import { clearAttachmentError, setAttachmentError } from '../../store/fieldError';
import { updateSessionStorage } from '../storage';
import type {
  ApiService,
  AttachmentKey,
  Attachments,
  HandleUploadCallbackParams,
  HandleUploadParams,
  OnCancelledCallback,
  OnRemovedCallback,
  OpenUploadDialogParams,
  RemoveFileParams,
  StoreState,
  TranslationFunction
} from './types';

export function openUploadDialog({
  dispatch,
  apiService,
  uploadDialog,
  onCancelled = () => null,
  field
}: OpenUploadDialogParams): () => void {
  return () => {
    dispatch(
      openDialog(
        uploadDialog(
          handleUpload({ dispatch, apiService, field, onCancelled }),
          handleCancelDialog(dispatch, onCancelled)
        )
      )
    );
  };
}

export function handleUpload({
  dispatch,
  apiService,
  field,
  onCancelled = () => null
}: HandleUploadParams): (params: HandleUploadCallbackParams) => void {
  const hcId = updateSessionStorage.getItem('hcId');
  return ({ attachments }: HandleUploadCallbackParams) => {
    const formData = new FormData();
    const sizeErrorFiles: File[] = [];
    let upload = false;
    const isDuplicate = checkDuplicateFile(attachments);
    if (attachments.staged.length + attachments.saved.length > 15) {
      return handleMaxLimitFileError(dispatch);
    }
    if (isDuplicate) {
      handleDuplicateFileError(dispatch);
    } else {
      [...attachments.staged].forEach(file => {
        //check if image is greater then 10 mb
        const fileSize = (file.size / (1024 * 1024)).toFixed(2);
        if (Number(fileSize) > 10) {
          sizeErrorFiles.push(file);
          handleSizeError(sizeErrorFiles, dispatch);
        } else {
          formData.append('files', file, file.name);
          upload = true;
        }
      });

      // Adding file type to sort files on ui
      formData.append('fileType', field);
      //Adding honeycomb id to upload document on correct request
      formData.append('hcId', hcId);
      if (!upload) {
        // if not uploading, perform as if the upload is "cancelled"
        onCancelled();
        handleSizeError(sizeErrorFiles, dispatch);
      } else {
        apiService
          .uploadAttachments(formData, dispatch)
          .then(() => {
            dispatch(clearAttachmentError(field));
          })
          .catch(() => {
            // if an error occurs during upload, cancel the upload
            handleCancelDialog(dispatch, onCancelled)();
            dispatch(openDialog(uploadErrorDialog()));
          });
      }
    }
  };
}

export function handleSizeError(sizeErrorFiles: File[], dispatch: Dispatch<AnyAction>): void {
  if (sizeErrorFiles.length) {
    dispatch(clearStagingAttachments());
    dispatch(openDialog(uploadFileSizeErrorDialog(sizeErrorFiles)));
  }
}

export function handleRemoveFile(
  dispatch: Dispatch<AnyAction>,
  apiService: ApiService,
  onRemoved: OnRemovedCallback = () => null
): (fileName: string) => void {
  return fileName => {
    dispatch(openDialog(removeFileDialog(removeFile({ dispatch, apiService, fileName, onRemoved }))));
  };
}

export function removeFile({ dispatch, apiService, fileName, onRemoved = () => null }: RemoveFileParams): () => void {
  return () => {
    apiService
      .removeAttachments(fileName, dispatch)
      .then(() => {
        dispatch(removeAttachments(fileName));
        onRemoved(fileName);
      })
      .catch(() => dispatch(generalErrorDialog() as unknown as AnyAction));
  };
}

export function handleCancelDialog(
  dispatch: Dispatch<AnyAction>,
  onCancelled: OnCancelledCallback = () => null
): () => void {
  return () => {
    dispatch(clearStagingAttachments());
    onCancelled();
  };
}

export function getUploadRequiredValidation(
  store: StoreState | null,
  key: AttachmentKey,
  autovalidate?: boolean,
  dispatch?: Dispatch<AnyAction>
): boolean {
  let required = false;
  const isAutovalidate = autovalidate || false;
  if (store) {
    const {
      customer: {
        soldTo: {
          address: { taxNumber1, naturalPerson: soldToNaturalPerson }
        },
        payer: {
          address: { naturalPerson: payerNaturalPerson }
        },
        billTo: {
          address: { naturalPerson: billToNaturalPerson }
        },
        shipTo: [shipToAddress],
        lineOfCredit,
        customerOptoutAttachments
      }
    } = store;
    const {
      address: { naturalPerson: shipToNaturalPerson }
    } = shipToAddress;
    switch (key) {
      case 'addressAttachment':
        required = true;
        isAutovalidate && dispatch && dispatch(updateAttachmentsValidation({ key, required }));
        break;
      case 'taxAttachment':
        required = !!taxNumber1;
        isAutovalidate && dispatch && dispatch(updateAttachmentsValidation({ key, required }));
        break;
      case 'ineAttachment':
        required = (soldToNaturalPerson || billToNaturalPerson || shipToNaturalPerson || payerNaturalPerson) as boolean;
        isAutovalidate && dispatch && dispatch(updateAttachmentsValidation({ key, required }));
        break;
      case 'constitutiveActAttachment':
        required = !soldToNaturalPerson;
        isAutovalidate && dispatch && dispatch(updateAttachmentsValidation({ key, required }));
        break;
      case 'financialStatementsAttachment':
        required = lineOfCredit as boolean;
        break;
      case 'financialCardAttachment':
        required = !customerOptoutAttachments;
        isAutovalidate && dispatch && dispatch(updateAttachmentsValidation({ key, required }));
        break;
      default:
        break;
    }
    return required;
  }
  return false;
}

export function onRemoveFile(
  field: string,
  required: boolean,
  files: unknown[],
  dispatch: Dispatch<AnyAction>,
  t: TranslationFunction
): void {
  if (required && files.length === 1) {
    dispatch(setAttachmentError({ [field]: t('errors.required') }));
  }
}

export function checkDuplicateFile(attachments: Attachments): boolean {
  let duplicate = false;
  const { saved = [], staged = [] } = attachments;
  const allFiles = [...saved, ...staged];
  staged.forEach(file => {
    const filterCurrentFile = allFiles.filter(item => item.name === file.name);
    if (filterCurrentFile.length > 1) {
      duplicate = true;
    }
  });
  return duplicate;
}

export function handleDuplicateFileError(dispatch: Dispatch<AnyAction>): void {
  dispatch(clearStagingAttachments());
  dispatch(openDialog(duplicateFileErrorDialog()));
}

export function handleMaxLimitFileError(dispatch: Dispatch<AnyAction>): void {
  dispatch(clearStagingAttachments());
  dispatch(openDialog(MaxLimitFileErrorDialog()));
}
